import { AdSlotType } from '../../../advertising/AdTypes';
import { WORLD_CONFIG } from '../../../config/world.config';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import { DEG_TO_RAD } from '../../../utils/math';
import { mirroredPlacements } from '../hazardBuilders';
import { COLLECTIBLE_FLOAT_HEIGHT, collectibleLanes, entryConnector, laneArea, laneCenterX, moduleSides, obstacleRow, standardLanes, straightExit } from '../segmentBuilders';
import {
  EnclosureKind,
  SegmentKind,
  type AdvertisingSlotDefinition,
  type CeilingZone,
  type FixedObstaclePlacement,
  type ObstacleVariant,
  type SegmentDefinition,
} from '../SegmentDefinition';
import { BACKDROP_ONLY } from './SEG_TUNNEL_40M';

/**
 * SEG_13 – interior fit-out 40 m (design/01_segments/SEG_13_Interior). The run leads through
 * the ground floor of a building under construction: drywall on both sides, a concrete
 * ceiling with light strips, a drywall partition with door openings and hanging cables.
 *
 *   Ceiling     z 0–40, underside 3.8 m (soft)
 *   Walls       drywall, inner faces x = ±3.95 m
 *   Partition   z 13–13.3: posts between the lanes (no lane change while passing), doors in
 *               lanes 0 and 2 (header 3.4 m, soft) and a low door in lane 1 (1.3 m → slide)
 *   Cables      z 29 over lanes 0–1, hanging down to 1.3 m (slide), lane 2 free
 *   Rows        D1 z 5 (drywall stack: lane change + cable drum), D2 z 21, D3 z 37 (cable drums: jump –
 *               8 m after door / cables, too close for a lane change at 18 m/s)
 *   Lighting    enclosure INTERIOR (construction lamps)
 *   Ads         banners on the drywall z 9 / z 31 ("Bauzaun-Banner (Innen)" / "Wandfläche")
 *
 * Floor, walls and building shell: greybox (assets/manifest/enclosureGreybox.ts).
 */
export const INTERIOR = {
  length: 40,
  ceiling: { clearance: 3.8, thickness: 0.3 },
  wall: { inner: 3.95, thickness: 0.3 },
  partition: {
    z: 13,
    thickness: 0.3,
    /** Posts between the lanes – full-height walls, clear of every running line. */
    postHalfWidth: 0.25,
    /** Wall piece from the outer door frame to the side wall. */
    outerEdge: 3.0,
    doorHeader: 3.4,
    lowDoor: { lane: 1, clearance: 1.3 },
  },
  cables: { z: 29, fromLane: 0, toLane: 1, clearance: 1.3, depth: 0.4 },
  rows: { D1: 5, D2: 21, D3: 37 },
  lights: { spacing: 5, rows: [-2.2, 0, 2.2] },
  // offset from the wall face: 6 cm (was 3) against z-fighting from afar (the ads add a depth offset)
  wallBanner: { y: 2.0, z: [9, 31], offset: 0.06 },
  /** Building shell around the interior (visual). */
  building: { halfWidth: 12, height: 8 },
} as const;

const ROW_TYPES = [ObstacleType.Jump, ObstacleType.LaneChange] as const;
const FACE_RIGHT = 90 * DEG_TO_RAD;
const FACE_LEFT = -90 * DEG_TO_RAD;

function partition(): CeilingZone[] {
  const P = INTERIOR.partition;
  const zFrom = P.z;
  const zTo = P.z + P.thickness;
  const zones: CeilingZone[] = [];
  const boundaries = Array.from({ length: WORLD_CONFIG.laneCount - 1 }, (_, i) => (laneCenterX(WORLD_CONFIG, i) + laneCenterX(WORLD_CONFIG, i + 1)) / 2);
  boundaries.forEach((x, i) => {
    zones.push({ id: `CEIL_PARTITION_POST_${i + 1}`, zFrom, zTo, xFrom: x - P.postHalfWidth, xTo: x + P.postHalfWidth, clearance: WORLD_CONFIG.groundHeight });
  });
  const outermost = { L: boundaries[0] ?? 0, R: boundaries[boundaries.length - 1] ?? 0 };
  for (const [side, sign] of [['L', -1], ['R', 1]] as const) {
    const wall = [sign * P.outerEdge, sign * INTERIOR.wall.inner];
    zones.push({ id: `CEIL_PARTITION_WALL_${side}`, zFrom, zTo, xFrom: Math.min(...wall), xTo: Math.max(...wall), clearance: WORLD_CONFIG.groundHeight });
    // normal door between the side wall piece and the post: its header only limits a double jump (soft)
    const door = [sign * P.outerEdge, outermost[side] + sign * P.postHalfWidth];
    zones.push({ id: `CEIL_DOOR_${side}`, zFrom, zTo, xFrom: Math.min(...door), xTo: Math.max(...door), clearance: P.doorHeader, soft: true });
  }
  const low = laneArea(WORLD_CONFIG, P.lowDoor.lane, P.lowDoor.lane, zFrom, zTo);
  zones.push({ id: 'CEIL_DOOR_LOW', zFrom, zTo, xFrom: low.xFrom + P.postHalfWidth, xTo: low.xTo - P.postHalfWidth, clearance: P.lowDoor.clearance });
  return zones;
}

function cables(): CeilingZone {
  const C = INTERIOR.cables;
  const area = laneArea(WORLD_CONFIG, C.fromLane, C.toLane, C.z - C.depth / 2, C.z + C.depth / 2);
  return { id: 'CEIL_CABLES', ...area, xFrom: C.fromLane === 0 ? -INTERIOR.wall.inner : area.xFrom, clearance: C.clearance };
}

const wallBanners: AdvertisingSlotDefinition[] = INTERIOR.wallBanner.z.flatMap((z, i) => {
  const s = i === 0 ? '' : `_${i + 1}`;
  const x = INTERIOR.wall.inner - INTERIOR.wallBanner.offset;
  return [
    { id: `AD_FENCE_LEFT${s}`, slotType: AdSlotType.FenceLeft, position: { x: -x, y: INTERIOR.wallBanner.y, z }, yaw: FACE_RIGHT, width: 6, height: 1.5 },
    { id: `AD_FENCE_RIGHT${s}`, slotType: AdSlotType.FenceRight, position: { x, y: INTERIOR.wallBanner.y, z }, yaw: FACE_LEFT, width: 6, height: 1.5 },
  ];
});

const at = (row: keyof typeof INTERIOR.rows, lane: number, obstacleId: string): FixedObstaclePlacement => ({ spawnPointId: `SOCKET_OBS_${row}_L${lane}`, obstacleId });
const STACK = 'OBS_MATERIAL_STACK_A';
const DRUM = 'OBS_CABLE_DRUM_A';
const PALLET = 'OBS_PALLET_A';

const STACK_LEFT: ObstacleVariant = { id: 'STACK_LEFT', weight: 1, placements: [at('D1', 0, STACK), at('D1', 2, DRUM), at('D2', 1, DRUM), at('D3', 0, DRUM), at('D3', 1, DRUM)] };
const STACK_RIGHT: ObstacleVariant = { id: 'STACK_RIGHT', weight: 1, placements: [at('D1', 1, DRUM), at('D1', 2, STACK), at('D2', 0, DRUM), at('D2', 2, DRUM), at('D3', 2, DRUM)] };
const STACK_CENTER: ObstacleVariant = { id: 'STACK_CENTER', weight: 1, placements: [at('D1', 1, STACK), at('D2', 0, PALLET), at('D2', 2, DRUM), at('D3', 2, PALLET)] };
const JUMP_LINE: ObstacleVariant = { id: 'JUMP_LINE', weight: 1, placements: [at('D1', 0, DRUM), at('D1', 1, PALLET), at('D2', 1, DRUM), at('D3', 0, PALLET), at('D3', 2, DRUM)] };
const mirror = (id: string, variant: ObstacleVariant): ObstacleVariant => ({ id, weight: variant.weight, placements: mirroredPlacements(WORLD_CONFIG, variant.placements) });

const INTERIOR_LAYOUTS: ObstacleVariant[] = [
  STACK_LEFT,
  STACK_RIGHT,
  STACK_CENTER,
  mirror('STACK_CENTER_MIRRORED', STACK_CENTER),
  JUMP_LINE,
  mirror('JUMP_LINE_MIRRORED', JUMP_LINE),
];

export const SEG_INTERIOR_40M: SegmentDefinition = {
  id: 'SEG_INTERIOR_40M',
  kind: SegmentKind.Straight,
  displayName: 'Innenausbau 40 m',
  length: INTERIOR.length,
  width: WORLD_CONFIG.trackWidth,
  visualAssetId: 'SEG_INTERIOR_40M',
  enclosure: { kind: EnclosureKind.Interior, zFrom: 0, zTo: INTERIOR.length },

  entry: entryConnector(),
  exits: [straightExit(INTERIOR.length)],
  lanes: standardLanes(WORLD_CONFIG),

  terrain: {
    surfaces: [],
    gaps: [],
    ceilings: [
      {
        id: 'CEIL_INTERIOR_CEILING',
        zFrom: 0,
        zTo: INTERIOR.length,
        xFrom: -INTERIOR.wall.inner,
        xTo: INTERIOR.wall.inner,
        clearance: INTERIOR.ceiling.clearance,
        soft: true,
      },
      ...partition(),
      cables(),
    ],
  },

  obstacleSpawnPoints: [
    ...obstacleRow(WORLD_CONFIG, 'D1', INTERIOR.rows.D1, ROW_TYPES),
    ...obstacleRow(WORLD_CONFIG, 'D2', INTERIOR.rows.D2, ROW_TYPES),
    ...obstacleRow(WORLD_CONFIG, 'D3', INTERIOR.rows.D3, ROW_TYPES),
  ],
  obstacleRowLayouts: { sparse: [], dense: [] },
  // Six layouts (user 2026-10-03: variety): a lane change only in D1, D2 / D3 jumps only (8 m behind door and cables).
  obstacleVariants: INTERIOR_LAYOUTS,

  collectibleSpawnPoints: collectibleLanes(WORLD_CONFIG, 1, INTERIOR.length - 1, 2, COLLECTIBLE_FLOAT_HEIGHT),

  decorationSockets: BACKDROP_ONLY(moduleSides(INTERIOR.length).decorationSockets),
  dynamicEventSockets: [],
  advertisingSlots: wallBanners,

  generation: {
    weight: 0.12,
    minDistance: 300,
    cooldownDistance: 300,
    minSegmentsAfterJunction: 1,
    family: 'ENCLOSED',
    tags: ['interior', 'enclosed', 'terrain', 'slide'],
  },
};
